/**
 * Fortschrittskarte eines Hintergrund-Auftrags — gemeinsame Regeln. (19.10.2026, Hintergrund-Fach Etappe 2)
 *
 * Ein Auftrag, den Vespera im Hintergrund laufen lässt (task_delegate, Tabelle background_tasks), zeigt im Chat EINE Karte:
 * Titel, Laufzeit, letzte Statuszeile, „Abbrechen“. Der Server meldet jede Änderung als Strom-Ereignis `job_fortschritt` an die
 * offenen Seiten DESSELBEN Kontos (server/job-fortschritt.ts). Kein Chat-Beitrag, nie im Prompt, nie ein Ergebnistext.
 */

/** Name des SSE-Ereignisses auf dem Chat-Strom. */
export const JOB_FORTSCHRITT_EREIGNIS = "job_fortschritt";
export const JOB_TITEL_HOECHSTENS = 60;
export const JOB_ZEILE_HOECHSTENS = 80;
/** Kennung eines Auftrags: so, wie background-task-store sie vergibt (UUID/Hex mit Strichen), nichts anderes. */
const KENNUNG = /^[A-Za-z0-9_-]{4,64}$/;

export type JobStatus = "running" | "done" | "failed";

export interface JobFortschritt {
  id: string;
  /** Kurztitel des Auftrags (aus dem Anfang des Auftrags, geschwärzt, eine Zeile). */
  titel: string;
  status: JobStatus;
  /** So lange läuft der Auftrag schon (ms, gemessen am Server); die Seite rechnet danach mit ihrer eigenen Uhr weiter (kein Uhren-Versatz). */
  laufMs: number;
  /** Nur bei done/failed: wie lange der Lauf dauerte. */
  dauerMs: number | null;
  /** Letzte Statuszeile („schreibt rechner.py …“) oder null. Eine Zeile, höchstens JOB_ZEILE_HOECHSTENS Zeichen. */
  zeile: string | null;
  /** Nur bei done: kommt das Ergebnis als Chat-Zeile (Schalter HINTERGRUND_CHAT_ERGEBNIS an)? Sonst sagt die Schlusszeile nichts darüber. */
  imChat: boolean;
  /**
   * 27.10.2026 (server/etappen-lauf.ts): „prozess“ = ein Etappen-Lauf (Arbeitszug im Hintergrund), kein Auftrag aus background_tasks. Sein
   * Stopp-Knopf geht an /api/chat/abort mit `stopp: true` statt an /api/background-tasks/:id/cancel. Fehlt es: Hintergrund-Auftrag.
   */
  art?: "prozess";
}

/** Leerraum glätten, Steuerzeichen raus, kürzen mit „…“. */
export function einzeilig(text: string, hoechstens: number): string {
  // eslint-disable-next-line no-control-regex
  const t = text.replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim();
  return t.length > hoechstens ? `${t.slice(0, hoechstens - 1).trimEnd()}…` : t;
}

/** Die Form aus einem SSE-Frame — nur die erwartete, sonst null. */
export function leseJobFortschritt(roh: unknown): JobFortschritt | null {
  if (!roh || typeof roh !== "object") return null;
  const { id, titel, status, laufMs, dauerMs, zeile, imChat, art } = roh as Record<string, unknown>;
  if (typeof id !== "string" || !KENNUNG.test(id)) return null;
  if (typeof titel !== "string" || !titel.trim() || titel.length > JOB_TITEL_HOECHSTENS) return null;
  if (status !== "running" && status !== "done" && status !== "failed") return null;
  if (typeof laufMs !== "number" || !Number.isFinite(laufMs) || laufMs < 0) return null;
  const dauer = typeof dauerMs === "number" && Number.isFinite(dauerMs) && dauerMs >= 0 ? dauerMs : null;
  const z = typeof zeile === "string" && zeile.trim() && zeile.length <= JOB_ZEILE_HOECHSTENS ? zeile : null;
  return { id, titel, status, laufMs, dauerMs: dauer, zeile: z, imChat: imChat === true, ...(art === "prozess" ? { art: "prozess" as const } : {}) };
}

/** „3 Min 05 s“, „45 s“, „1 Std 02 Min“ — nie negativ. */
export function laufzeitText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} Min ${String(s % 60).padStart(2, "0")} s`;
  return `${Math.floor(min / 60)} Std ${String(min % 60).padStart(2, "0")} Min`;
}
